import * as React from "react";
import { withTheme } from "styled-components";

import { withDefault } from "collection-utils";

import { Wrapper, OverlayScroller } from "./OverlayEditorStyle";
import { ThemedModalNavBar } from "../../chrome/smart/nav-bar/nav-bar";
import { Icon } from "common/components/icons";
import { MenuItem } from "../../lib/menu-item";
import { RuntimeTheme } from "../../lib/theme/theme";
import GrowingEntryView from "../views/growing-entry/growing-entry-view";
import { getLocalizedString } from "common/components/localization";
import { RenderEnvironment } from "common/components/types";

interface Props {
    closed?: () => void;
    updated?: (text: string) => void;
    saved?: (text: string) => void | undefined;

    text?: string;
    placeholder?: string;
    title: string;
    saveText?: string;
    saveIcon?: Icon;
    closedText?: string;
    theme: RuntimeTheme;
    allowEmpty: boolean;
    renderEnvironment: RenderEnvironment;
}

interface State {
    text: string;
}

class OverlayEditor extends React.Component<Props, State> {
    public state: State = {
        text: withDefault(this.props.text, ""),
    };

    private inputRef = React.createRef<any>();

    private onInput = (ev: React.ChangeEvent<HTMLTextAreaElement>): void => {
        const text = ev.currentTarget.value;
        this.setState({ text });

        if (this.props.updated !== undefined) {
            this.props.updated(text);
        }
    };

    private onCloseClicked = () => {
        if (this.props.closed !== undefined) {
            this.props.closed();
        }
    };

    private onSaveClicked = () => {
        const { saved, allowEmpty } = this.props;
        if (saved === undefined) return;
        if (!allowEmpty && this.state.text.length === 0) return;

        saved(this.state.text);
    };

    private onClickRegionClicked = (ev: React.MouseEvent<HTMLDivElement>) => {
        if (this.inputRef.current !== null && ev.currentTarget === ev.target) {
            this.inputRef.current.focus();
        }
    };

    public shouldComponentUpdate(nextProps: Readonly<Props>, nextState: Readonly<State>, _nextContent: any) {
        return (
            nextState.text !== this.state.text ||
            nextProps.title !== this.props.title ||
            nextProps.placeholder !== this.props.placeholder ||
            nextProps.theme !== this.props.theme
        );
    }

    public render(): React.ReactNode {
        const { placeholder, title, saved, saveIcon, saveText, closedText, allowEmpty, renderEnvironment } = this.props;

        const menuItems: MenuItem[] = [];

        const closeButton: MenuItem = {
            title: withDefault(closedText, getLocalizedString("close")),
            icon: "00-01-glide-close",
            onClick: this.onCloseClicked,
            style: "platform-cancel",
        };

        menuItems.push(closeButton);

        if (saved !== undefined && saveIcon !== undefined && saveText !== undefined) {
            const saveButton: MenuItem = {
                title: saveText,
                icon: saveIcon,
                onClick: this.onSaveClicked,
                style: "platform-submit",
                isEnabled: allowEmpty || this.state.text.length > 0,
            };

            menuItems.push(saveButton);
        }

        const marginBottom = this.props.theme.navBar.style === "Android" ? 75 : 0;
        const style = {
            marginBottom,
        };

        const className = renderEnvironment.sizeClass === "tablet" ? "modal-anim-tag-tablet" : "modal-anim-tag";
        return (
            <>
                <Wrapper data-test="app-overlay-editor" className={className} onClick={this.onClickRegionClicked}>
                    <OverlayScroller style={style}>
                        <GrowingEntryView
                            inputRef={this.inputRef}
                            onChange={this.onInput}
                            value={this.state.text}
                            placeholder={placeholder}
                        />
                    </OverlayScroller>
                </Wrapper>
                <ThemedModalNavBar renderEnvironment={renderEnvironment} title={title} barButtonItems={menuItems} />
            </>
        );
    }
}

export default withTheme(OverlayEditor);
